import { useAnimation } from 'framer-motion';
import { cardTextOn } from '@/features/colors/lib/paletteLookup';
import { Box, MotionButton } from '@/ui';
import { useSparkleBurst } from './useSparkleBurst';

type Props = {
  color: string;
  isActive: boolean;
  onClick: () => void;
  // Hover preview (mouse-only affordance — touch users tap to apply).
  onHoverStart?: () => void;
  onHoverEnd?: () => void;
};

// Four-point sparkle with two mini sparks — marks the active accent swatch.
const SparkleIcon = () => (
  <svg
    fill="none"
    height="20"
    stroke="currentColor"
    strokeLinecap="round"
    strokeLinejoin="round"
    strokeWidth="1.7"
    viewBox="0 0 24 24"
    width="20"
  >
    <path d="M12 4c.4 3.6 1.6 6 8 8c-6.4 2-7.6 4.4-8 8c-.4-3.6-1.6-6-8-8c6.4-2 7.6-4.4 8-8z" />
    <path d="M20 3.2v2.8M18.6 4.6h2.8" strokeWidth="1.5" />
    <path d="M4.4 18v2.4M3.2 19.2h2.4" strokeWidth="1.5" />
  </svg>
);

export const ColorSwatch = ({
  color,
  isActive,
  onClick,
  onHoverStart,
  onHoverEnd,
}: Props) => {
  const controls = useAnimation();
  const { burst, reduceMotion } = useSparkleBurst();
  // Light swatches flip the sparkle to ink with a white glow so it stays legible.
  const isLightSwatch = cardTextOn(color) !== '#ffffff';

  const handleClick = (e: React.MouseEvent<HTMLElement>) => {
    onClick();
    if (!reduceMotion) {
      controls.start({
        scale: [1, 1.3, 0.92, 1],
        transition: {
          duration: 0.42,
          ease: [0.34, 1.56, 0.64, 1],
          times: [0, 0.35, 0.68, 1],
        },
      });
      burst(e.currentTarget, color);
    }
  };

  return (
    <MotionButton
      animate={controls}
      aria-label={`Select accent color ${color}`}
      aria-pressed={isActive}
      cursor="pointer"
      position="relative"
      title={color}
      type="button"
      onClick={handleClick}
      onMouseEnter={onHoverStart}
      onMouseLeave={onHoverEnd}
    >
      <Box
        _hover={{ transform: 'translateY(-1px)' }}
        alignItems="center"
        bg={color}
        border="1.5px solid"
        borderColor={isActive ? 'ink.strong' : 'transparent'}
        borderRadius="6px"
        color={isLightSwatch ? 'ink.strong' : 'white'}
        display="flex"
        justifyContent="center"
        overflow="hidden"
        sx={{ aspectRatio: '1 / 1' }}
        transition="border-color 0.15s, transform 0.12s"
      >
        <Box
          as="span"
          display="flex"
          opacity={isActive ? 1 : 0}
          filter={
            isLightSwatch
              ? 'drop-shadow(0 0 1.5px rgba(255,255,255,0.85))'
              : 'drop-shadow(0 1px 1.5px rgba(0,0,0,0.28))'
          }
        >
          <SparkleIcon />
        </Box>
      </Box>
    </MotionButton>
  );
};
